import { type RefObject, useEffect, useId, useState } from 'react';

import type { GlassMaterial } from '@typings/glass';
import { createDisplacementMap } from '@utils/displacementMap';

export interface LiquidFilterState {
  /** A valid CSS/SVG identifier, unique per surface. */
  readonly id: string;
  readonly width: number;
  readonly height: number;
  readonly mapUrl: string;
}

/**
 * React's useId output contains characters that are legal in an HTML id
 * but not inside a CSS `url(#...)` reference without escaping.
 */
const toFilterId = (reactId: string): string => `lg-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`;

/**
 * Builds and maintains the refraction map for one surface.
 *
 * The map depends on the surface's exact pixel size and corner radius, so it
 * is rebuilt whenever the element resizes. Resizes are coalesced to one per
 * animation frame; a window drag would otherwise rebuild on every
 * ResizeObserver callback.
 *
 * Returns null until the first measurement, and whenever `enabled` is false,
 * so callers render the frost fallback rather than a filter with no map.
 */
export const useLiquidFilter = (
  ref: RefObject<HTMLElement | null>,
  material: GlassMaterial,
  enabled: boolean,
): LiquidFilterState | null => {
  const id = toFilterId(useId());
  const [state, setState] = useState<LiquidFilterState | null>(null);
  const { bezel } = material;

  useEffect(() => {
    const element = ref.current;
    if (!enabled || !element) {
      setState(null);
      return;
    }

    let frame = 0;

    const rebuild = (): void => {
      frame = 0;
      const width = Math.round(element.offsetWidth);
      const height = Math.round(element.offsetHeight);
      if (width === 0 || height === 0) return;

      const radius = parseFloat(getComputedStyle(element).borderTopLeftRadius) || 0;
      const mapUrl = createDisplacementMap({ width, height, radius, bezel });

      setState((previous) =>
        previous?.mapUrl === mapUrl && previous.width === width && previous.height === height
          ? previous
          : { id, width, height, mapUrl },
      );
    };

    const observer = new ResizeObserver(() => {
      if (frame === 0) frame = requestAnimationFrame(rebuild);
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [ref, enabled, bezel, id]);

  return enabled ? state : null;
};
